import React, { useCallback, useRef, useEffect } from 'react';
import classnames from 'classnames';

export enum EUITextAreaResizeType {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
  BOTH = 'both',
  NONE = 'none',
  AUTO = 'auto', // New option for auto-resize
}

export interface IUITextAreaProps {
  label?: string;
  value: string;
  id?: string;
  rows?: number;
  limit?: number;
  aboutText?: string;
  className?: string;
  labelClassName?: string;
  textAreaClassName?: string;
  labelStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  errorMessage?: string | null;
  errorClassName?: string;
  warning?: boolean;
  disabled?: boolean;
  resizable?: EUITextAreaResizeType;
  placeholder?: string;
  name?: string;
  autoFocus?: boolean;
  onChange?: (value: string) => void;
  autoResize?: boolean; // New prop for auto-resizing
  minRows?: number; // used in conjunction with autoResize
}

export const UITextArea = (props: IUITextAreaProps) => {
  const {
    label,
    id = 'cancellation-policies-text',
    rows = 5,
    limit = 0,
    aboutText,
    onChange,
    resizable = EUITextAreaResizeType.NONE,
    className,
    errorMessage,
    labelClassName,
    textAreaClassName,
    errorClassName,
    autoResize = false, // Default to false
    minRows = 1, // Default to 1 row
    ...rest
  } = props;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Function to adjust the height of textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || !autoResize) return;

    // Store the original overflow
    const originalOverflow = textarea.style.overflow;
    
    // Reset height temporarily to get the correct scrollHeight
    textarea.style.height = 'auto';
    textarea.style.overflow = 'hidden';
    
    // Force a reflow to ensure scrollHeight is calculated correctly
    void textarea.offsetHeight;
    
    // Set the height to scrollHeight to expand the textarea
    const newHeight = Math.max(textarea.scrollHeight, textarea.clientHeight);
    textarea.style.height = `${newHeight}px`;
    
    // Restore original overflow
    textarea.style.overflow = originalOverflow;
  }, [autoResize]);

  // Adjust height on initial render and when content changes
  useEffect(() => {
    adjustHeight();
  }, [props.value, adjustHeight]);

  // Also adjust height after mount to ensure proper initial sizing
  useEffect(() => {
    if (autoResize) {
      // Use setTimeout to ensure the DOM has fully rendered
      const timeoutId = setTimeout(() => {
        adjustHeight();
      }, 0);
      
      // Also use requestAnimationFrame for better timing
      const rafId = requestAnimationFrame(() => {
        adjustHeight();
      });
      
      return () => {
        clearTimeout(timeoutId);
        cancelAnimationFrame(rafId);
      };
    }
  }, [adjustHeight, autoResize]);

  // Use ResizeObserver to handle any parent size changes
  useEffect(() => {
    if (!autoResize || !textareaRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      adjustHeight();
    });

    resizeObserver.observe(textareaRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [adjustHeight, autoResize]);

  const isError = !!errorMessage || errorMessage === '';

  // Determine if we should use auto-resize or manual resize
  const effectiveResizable = autoResize
    ? EUITextAreaResizeType.NONE // When auto-resize is true, disable manual resizing
    : resizable;

  const inputClassname = classnames(
    'font-pt-sans text-[15px] p-[10px] w-full outline-none text-black border border-solid',
    textAreaClassName,
    {
      'default:border-gray-40 default:bg-ivory': !isError,
      'border-red-95 bg-red-25': isError,
      'resize-none': effectiveResizable === EUITextAreaResizeType.NONE,
      'resize-y': effectiveResizable === EUITextAreaResizeType.VERTICAL,
      'resize-x': effectiveResizable === EUITextAreaResizeType.HORIZONTAL,
      resize: effectiveResizable === EUITextAreaResizeType.BOTH,
      'overflow-hidden': autoResize, // Hide scrollbar when auto-resizing
    }
  );

  const handleChange = useCallback(
    e => {
      if (autoResize) {
        adjustHeight();
      }
      onChange && onChange(e.target.value);
    },
    [onChange, autoResize, adjustHeight]
  );

  const currentTextLength = props.value?.length ?? 0;
  const remainingLength = limit - currentTextLength > 0 ? limit - currentTextLength : 0;
  const limitText = limit ? `Limit: ${limit} characters / Remaining: ${remainingLength}` : '';

  return (
    <div className={`flex flex-col ${className ?? ''}`}>
      {label && (
        <label
          style={props.labelStyle}
          className={classnames('text-black font-pt-sans text-13px leading-17px tracking-2xs mb-5px', labelClassName)}
          htmlFor={id}
        >
          {label}
        </label>
      )}
      <textarea
        ref={textareaRef}
        rows={autoResize ? minRows : rows} // Start with 1 row when auto-resizing
        className={inputClassname}
        id={id}
        onChange={handleChange}
        {...rest}
      />
      <div className="flex justify-between items-start">
        <p className={`text-gray-80 text-[14px] leading-[18px] font-pt-sans mt-5px mb-0`}>{aboutText}</p>
        {limitText && <p className={`text-gray-80 text-[14px] leading-[18px] font-pt-sans mt-5px mb-0`}>{limitText}</p>}
      </div>
      {errorMessage && (
        <p className={`font-normal text-red-95 text-13px leading-2xs font-pt-sans mt-5px mb-0 ${errorClassName ?? ''}`}>
          {errorMessage}
        </p>
      )}
    </div>
  );
};
